import type { AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { Directive, ElementRef, Input, Renderer2, inject } from '@angular/core';

@Directive({
    selector: '[spFallbackImg]',
    standalone: true,
})
export class SppFallbackImgDirective implements AfterViewInit, OnChanges {
    private el = inject(ElementRef);
    private renderer = inject(Renderer2);

    @Input('spFallbackImg') public fallbackUrl!: string;

    private imgElement!: HTMLImageElement;

    /**
     * Tracks the error-handling phase:
     *   0 — no error yet
     *   1 — first error handled (sources removed, img src retried)
     *   2 — fallback applied (img src also failed)
     */
    private errorPhase: 0 | 1 | 2 = 0;

    public ngAfterViewInit(): void {
        this.imgElement = this.el.nativeElement.querySelector('img');

        if (this.imgElement) {
            this.renderer.listen(this.imgElement, 'error', () => this.handleImgError());
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['fallbackUrl'] && !changes['fallbackUrl'].firstChange) {
            this.errorPhase = 0;
        }
    }

    private handleImgError(): void {
        if (this.errorPhase === 0) {
            // Phase 0 → 1: A <source> element may have caused the error.
            // Remove all <source> elements and let the browser retry with the <img> src.
            const sources = this.el.nativeElement.querySelectorAll('source') as NodeListOf<HTMLSourceElement>;
            sources.forEach((node) => node.remove());

            this.errorPhase = 1;

            // Force the browser to re-evaluate the <img> src without <source> interference.
            const currentSrc = this.imgElement.getAttribute('src');
            if (currentSrc) {
                this.renderer.setAttribute(this.imgElement, 'src', '');
                // Use setTimeout so the empty-src assignment is processed first,
                // then the browser treats the next assignment as a fresh load.
                setTimeout(() => {
                    this.renderer.setAttribute(this.imgElement, 'src', currentSrc);
                }, 0);
            } else {
                // No src at all — go straight to fallback.
                this.errorPhase = 2;
                this.renderer.setAttribute(this.imgElement, 'src', this.fallbackUrl);
            }
        } else if (this.errorPhase === 1) {
            // Phase 1 → 2: The <img> src itself failed. Apply fallback.
            this.errorPhase = 2;
            this.renderer.setAttribute(this.imgElement, 'src', this.fallbackUrl);
        }
        // Phase 2: fallback already applied — nothing to do.
    }
}
